import React, { useEffect, useState } from 'react';
import { getPublicSettings, verifyGateCode } from '../lib/api';
import { Lock, Loader2, HardHat } from 'lucide-react';

/**
 * "Lavori in corso" access gate.
 *
 * Behaviour:
 *  - On mount, calls GET /api/settings/public and reads only the
 *    boolean `gate_enabled`. The actual access code is NEVER sent to
 *    the frontend.
 *  - If `gate_enabled === false`, the children are rendered normally.
 *  - If `gate_enabled === true` AND this browser session has not been
 *    unlocked yet, a full-screen prompt asks for the password. The
 *    code is validated via POST /api/gate/verify (server-side only).
 *    On success we store an unlock flag in sessionStorage and render
 *    the app. The flag is wiped automatically when the tab is closed.
 */
const SESSION_UNLOCK_KEY = 'wallweek_gate_unlocked';

const Gate = ({ children }) => {
  const [status, setStatus] = useState('loading'); // 'loading' | 'open' | 'locked'
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getPublicSettings();
        if (cancelled) return;
        if (!s?.gate_enabled || window.location.pathname.startsWith('/simulazione/')) {
          setStatus('open');
          return;
        }
        try {
          if (sessionStorage.getItem(SESSION_UNLOCK_KEY) === '1') {
            setStatus('open');
            return;
          }
        } catch { /* sessionStorage may be unavailable */ }
        setStatus('locked');
      } catch {
        // If the public settings call fails we fail-open so the app
        // stays usable (matches the behaviour everywhere else in the
        // codebase where getPublicSettings errors are swallowed).
        if (!cancelled) setStatus('open');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setError('Inserisci la password');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await verifyGateCode(code.trim());
      try { sessionStorage.setItem(SESSION_UNLOCK_KEY, '1'); } catch { /* noop */ }
      setStatus('open');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Codice non valido');
    } finally {
      setBusy(false);
    }
  };

  if (status === 'loading') {
    // Transparent placeholder while we fetch the public flag. The app's
    // Splash component is already covering the viewport at this point, so
    // we deliberately render nothing to avoid any visible flash.
    return null;
  }

  if (status === 'open') {
    return children;
  }

  // Locked — full-screen "Lavori in corso" prompt
  return (
    <div
      data-testid="gate-screen"
      className="fixed inset-0 z-[2147483000] flex items-center justify-center p-4"
      style={{
        background:
          'radial-gradient(1200px 600px at 50% -10%, #1e4d80 0%, #0d2440 60%, #06121f 100%)',
      }}
    >
      <div
        className="w-full max-w-[420px] bg-white rounded-2xl shadow-2xl ring-1 ring-black/10 p-6"
      >
        <div className="flex items-center gap-2 mb-2">
          <span className="w-10 h-10 rounded-full bg-[#e56b3f] text-white flex items-center justify-center">
            <HardHat size={20} />
          </span>
          <div>
            <h1 className="font-black text-gray-900 text-lg leading-tight">
              Lavori in corso
            </h1>
            <p className="text-[12px] text-gray-500">
              Wall Week è temporaneamente riservato ai beta tester
            </p>
          </div>
        </div>
        <p className="mt-3 text-[13px] text-gray-700 leading-snug">
          Inserisci la password di accesso che hai ricevuto per continuare.
        </p>
        <form onSubmit={submit} className="mt-4 space-y-2">
          <div className="relative">
            <Lock
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="password"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Password di accesso"
              autoFocus
              data-testid="gate-password-input"
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border-2 border-gray-200 focus:border-[#1e4d80] outline-none text-sm"
            />
          </div>
          {error && (
            <p
              className="text-[12px] text-red-600 font-semibold"
              data-testid="gate-error"
            >
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="gate-submit"
            className="w-full bg-[#1e4d80] hover:bg-[#153a63] disabled:opacity-70 text-white font-bold py-2.5 rounded-xl shadow-lg tracking-wider active:scale-95 transition flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : null}
            {busy ? 'VERIFICA…' : 'ENTRA'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Gate;
